import { createContext, useContext, ReactNode, useState } from 'react';
import { DeliveryOption } from 'typescript/components/select_and_reveal/types';

interface CheckoutContextType {
  orderNumber: string;
  applicationId: string;
  locationId: string;
  recaptchaKey: string;
  trackingData: Record<string, any>;
  deliveryOptions: Record<DeliveryOption, boolean>;
  deliveryOption: number | null;
  setDeliveryOption: (deliveryOption: number | null) => void;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(
  undefined,
);

/*
  Custom hook to use checkout flow utils
 */
const useCheckoutFlow = () => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error(
      'useCheckoutFlow must be used within a CheckoutFlowProvider',
    );
  }
  return context;
};

interface CheckoutFlowProviderProps {
  children: ReactNode;
  orderNumber: string;
  applicationId: string;
  locationId: string;
  recaptchaKey: string;
  deliveryOptions: Record<DeliveryOption, boolean>;
  trackingData: Record<string, any>; // TODO: Define the shape of tracking data
}

/*
  Provides a component that wraps the checkout flow with some shared state.
  NOTE: This must be used within a LocaleInfoProvider.
 */
const CheckoutFlowProvider = ({
  children,
  orderNumber,
  applicationId,
  locationId,
  recaptchaKey,
  deliveryOptions,
  trackingData,
}: CheckoutFlowProviderProps) => {
  const [deliveryOption, setDeliveryOption] = useState<number | null>(null);

  return (
    <CheckoutContext.Provider
      value={{
        orderNumber,
        applicationId,
        locationId,
        recaptchaKey,
        trackingData,
        deliveryOptions,
        deliveryOption,
        setDeliveryOption,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export { CheckoutFlowProvider, useCheckoutFlow };
